import { createSelector } from '@reduxjs/toolkit';
import toLocaleTime from '@sv-support-sdk/utils/src/helpers/toLocaleTime';
import fileSizeFormat from '@sv-support-sdk/utils/src/helpers/fileSizeFormat';
import { ticketsSelectors } from '@sv-support-sdk/store';
import { IMessage, ITicket } from '@sv-support-sdk/shared-types/src/common/tickets';

import { RootState } from '..';
import { getTicketStatus } from '../../helpers/tickets';
import { selectUserAvatar } from './user';

export interface ITicketSelector extends Omit<ITicket, 'status'> {
  createdAtToShow: string;
  status: ReturnType<typeof getTicketStatus>;
}

export const {
  selectTickets,
  selectTicketsMeta,
  selectTicketsLoading,
  selectSelectedTicketLoading,
  selectSelectedTicket,
  selectAttachmentsLoading,
  selectReplyAttachments,
  selectTicketsHash,
  selectUnreadMessages,
  selectTicketCreationLoading,
} = ticketsSelectors;

export const selectActiveTab = (state: RootState) => state.tickets.activeTab;

const getAuthorEmail = ({ author }: IMessage) => {
  return author.name && author.name === author.email ? '' : author.email;
};

export const ticketsSelector = createSelector(selectTickets, (tickets) => {
  return tickets.map((ticket): ITicketSelector => {
    return {
      ...ticket,
      createdAtToShow: toLocaleTime(ticket.createdAt),
      status: getTicketStatus(ticket, ticket.closedAt),
    };
  });
});

export const ticketSelector = createSelector(
  [selectSelectedTicket, selectUserAvatar],
  (ticket, userAvatar) => {
    if (!ticket) return null;

    const { initialMessage } = ticket;

    return {
      ...ticket,
      status: getTicketStatus(ticket, ticket.closedAt),
      initialMessage: {
        ...initialMessage,
        author: {
          ...initialMessage.author,
          email: getAuthorEmail(ticket.initialMessage),
        },
      },
      messages: ticket.messages
        .map((message) => {
          const { author } = message;

          return {
            ...message,
            author: {
              ...author,
              avatar: author.type === 'User' ? userAvatar : author.avatar,
              email: getAuthorEmail(message),
            },
            attachments: message.attachments.map((attachment) => ({
              ...attachment,
              size: fileSizeFormat(attachment.size as number),
            })),
          };
        })
        .sort((a, b) => Number(new Date(a.createdAt)) - Number(new Date(b.createdAt))),
    };
  },
);
